import { useState, type ReactNode } from "react";
import { C, studioLevels, annexes, stores } from "@bd/content";
import { cashForecast, creditLimit, money, netWorth, palletCapacity, palletsUsed, priceIndex, type Company, rivalPrice, storeWeekly, buyoutBlocked, rivalActive, BALANCE, eraCostMult, yearOf } from "@bd/sim";
import { Icon, Sheet, Why } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { storageLines, weeklyCashLines } from "../lib/why";
import { t, tp } from "../i18n";

/** The rules engine's buyout reasons, in the player's language. */
function blockedText(why: string): string {
  const max = /^Regulators won't let you buy a (\d+)th publisher: you already own (\d+)\.$/.exec(why);
  if (max) return t("No more buyouts: regulators cap you at {n}.", { n: max[2] });
  const wait = /^You're still folding in your last buyout: the next can come in (\d+) years?\.$/.exec(why);
  if (wait) return tp(Number(wait[1]), "Next buyout in {n} year", "Next buyout in {n} years");
  return t(why);
}

function CashChart({ co, game }: { co: Company; game: NonNullable<ReturnType<typeof useGame.getState>["game"]> }) {
  const past = co.ledger.map((w) => w.income - w.costs);
  let cash = co.cash;
  const back: number[] = [];
  for (let i = past.length - 1; i >= 0; i--) {
    back.unshift(cash);
    cash -= past[i];
  }
  const ahead = cashForecast(game, co, 26).map((w) => w.cash);
  const all = [...back, ...ahead];
  const w = 640;
  const h = 190;
  const lineH = 128; // the cash line on top, weekly money in and out underneath
  // fit the scale to the money, not to zero: $250M against a zero line is one flat stroke at the top
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const span = Math.max(1, hi - lo);
  const min = lo < 0 || lo < span * 0.6 ? Math.min(0, lo) : lo - span * 0.15;
  const max = hi + span * 0.1;
  const x = (i: number) => (i / Math.max(1, all.length - 1)) * w;
  const y = (v: number) => lineH - 8 - ((v - min) / Math.max(1, max - min)) * (lineH - 22);
  const path = (arr: number[], off: number) => arr.map((v, i) => `${i ? "L" : "M"}${x(i + off).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const flows = co.ledger.slice(-back.length);
  // scale to a typical big week, not the one odd week (a buyout, a huge reprint), which is clipped
  const sizes = flows.flatMap((f) => [Math.abs(f.income), Math.abs(f.costs)]).sort((p, q) => p - q);
  const big = Math.max(1, sizes[Math.floor(sizes.length * 0.9)] ?? 1);
  const barW = Math.max(1, w / Math.max(1, all.length) - 1);
  const mid = lineH + (h - lineH) / 2;
  const half = (h - lineH) / 2 - 4;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img" aria-label={t("Cash over the past year and the next six months, with money in and out each week")}>
      {min <= 0 && <line x1={0} x2={w} y1={y(0)} y2={y(0)} stroke="var(--line)" strokeWidth={1.5} />}
      <text x={4} y={y(max) + 18} fontSize={18} fill="var(--muted)">{money(Math.round(max))}</text>
      <text x={4} y={y(min) - 6} fontSize={18} fill="var(--muted)">{money(Math.round(min))}</text>
      <path d={path(back, 0)} fill="none" stroke="var(--gold)" strokeWidth={3} />
      <path d={path([back[back.length - 1] ?? co.cash, ...ahead], back.length - 1)} fill="none" stroke="var(--gold)" strokeWidth={2.5} strokeDasharray="6 5" />
      <line x1={x(back.length - 1)} x2={x(back.length - 1)} y1={0} y2={h} stroke="var(--muted)" strokeDasharray="2 4" />
      <circle cx={x(back.length - 1)} cy={y(back[back.length - 1] ?? co.cash)} r={5} fill="var(--gold)" stroke="var(--paper)" strokeWidth={2} />
      {/* each week: money in above the middle line, money out below */}
      <line x1={0} x2={x(back.length - 1)} y1={mid} y2={mid} stroke="var(--line)" />
      {flows.map((f, i) => (
        <g key={i}>
          <rect x={x(i) - barW / 2} y={mid - (Math.min(big, Math.max(0, f.income)) / big) * half} width={barW} height={(Math.min(big, Math.max(0, f.income)) / big) * half} fill="var(--green)" opacity={0.7} />
          <rect x={x(i) - barW / 2} y={mid} width={barW} height={(Math.min(big, Math.max(0, f.costs)) / big) * half} fill="var(--bad)" opacity={0.55} />
        </g>
      ))}
    </svg>
  );
}

export function Finances() {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  const co = me(game)!;
  const limit = creditLimit(game, co);
  const [amount, setAmount] = useState(Math.round(Math.min(limit, 5000 * priceIndex(game.week)) / 100) * 100);
  const week = weeklyCashLines(co);
  const weekLines = week.lines.map((l) => ({ ...l, label: t(l.label) }));
  const year = co.ledger.reduce((a, w) => ({ in: a.in + w.income, out: a.out + w.costs }), { in: 0, out: 0 });
  const next = studioLevels[studioLevels.findIndex((l) => l.id === co.level) + 1];
  const idx = priceIndex(game.week);
  const run = async (c: Parameters<typeof cmd>[0], ok: string) => {
    const r = await cmd(c);
    if (!r.ok) toast({ title: t("Not possible"), text: r.error ? blockedText(r.error) : r.error, tone: "warn" });
    else toast({ title: ok, tone: "good" });
  };
  const used = palletsUsed(co);
  const cap = palletCapacity(co);
  const title = (
    <span className="biz-title">
      {t("Finances")}
      <span className="biz-chip" title={t("Net worth")}><Icon name="coin" size={14} color="var(--gold)" />{money(netWorth(co, game.week))}</span>
    </span>
  );
  return (
    <Sheet title={title} onClose={() => set({ screen: null })} drawer testId="finances">
      <div className="grid2 fin-grid" style={{ gridTemplateColumns: "2fr 1fr" }}>
        <div className="stack">
          <CashChart co={co} game={game} />
          <div className="grid2 biz-stats">
            <div className="inset biz-stat num" title={t("Cash now")}><Icon name="coin" size={16} color="var(--gold)" />{money(co.cash)}</div>
            <div className="inset biz-stat num good" title={t("Income, 52 wk")}><Icon name="trend" size={16} />{money(year.in)}</div>
            <div className="inset biz-stat num bad" title={t("Costs, 52 wk")}><Icon name="trend" size={16} style={{ transform: "scaleY(-1)" }} />{money(year.out)}</div>
            <div className={`inset biz-stat num${used > cap ? " bad" : ""}`} title={used > cap ? t("{n} pallets · space for {cap} · the rest stored outside (fee)", { n: used, cap }) : t("{n} pallets · space for {cap}", { n: used, cap })}>
              <Icon name="box" size={16} /><span>{used}<span className="muted" style={{ fontWeight: 400 }}>/{cap}</span></span>
              <Why title={t("Warehouse")} lines={storageLines(game, co).map((l) => ({ ...l, label: t(l.label), note: l.note ? t(l.note) : l.note }))} />
            </div>
          </div>
          <span className="caps">{t("Last week")}</span>
          <table className="list biz-ledger">
            <tbody>
              {weekLines.map((l) => <tr key={l.label}><td>{l.label}</td><td className="num" style={{ textAlign: "right" }}>{money(l.value as number)}</td></tr>)}
              <tr className="total"><td>{t("Net")}</td><td className={`num ${week.total >= 0 ? "good" : "bad"}`} style={{ textAlign: "right" }}>{money(week.total)}</td></tr>
            </tbody>
          </table>
          <RdBudget />
        </div>
        <div className="stack">
          {co.loans.map((l) => (
            <div key={l.id} className="card spread biz-card" title={t("{p}% a year", { p: Math.round(l.rate * 52 * 100) })}>
              <span className="stack" style={{ gap: 2, minWidth: 0 }}>
                <strong>{l.lender === "gran" ? "Gran Okafor" : l.lender === "bank" ? t("Bank") : t("Investor")}</strong>
                <span className="biz-line"><span className="biz-ic num">{t("{m} left", { m: money(l.principal) })}</span><span className="biz-ic num">{t("{pay}/wk", { pay: money(l.payment) })}</span></span>
              </span>
              <button className="btn small" onClick={() => run({ type: "repay", loanId: l.id }, t("Loan repaid"))} disabled={co.cash < l.principal}>{t("Repay")}</button>
            </div>
          ))}
          <div className="card stack biz-card">
            <span className="spread">
              <strong className="biz-ic"><Icon name="coin" size={16} color="var(--gold)" />{t("Bank")}</strong>
              <span className="biz-ic num muted" title={t("The bank lends up to")}>≤ {money(limit)} <Why title={t("How much the bank lends")} lines={[{ label: t("Any studio"), value: money(6000 * idx) }, { label: t("Your sales"), value: money(year.in * 0.3) }, { label: t("Your reputation"), value: money(co.reputation * 60 * idx) }, { label: t("Already borrowed"), value: money(-co.loans.filter((l) => l.lender === "bank").reduce((a, l) => a + l.principal, 0)) }]} total={money(limit)} /></span>
            </span>
            <input type="range" className="slider" min={0} max={Math.max(100, limit)} step={100} value={Math.min(amount, limit)} onChange={(e) => setAmount(Number(e.target.value))} aria-label={t("Loan amount")} />
            <button className="btn small" disabled={amount <= 0 || amount > limit} onClick={() => run({ type: "loan", amount }, t("Borrowed {m}", { m: money(amount) }))}>{t("Borrow {m} · 2 yr · 9%", { m: money(Math.min(amount, limit)) })}</button>
          </div>
          {next && (
            <Bet
              name={t(next.name)}
              tip={`${t("Room for {n} people", { n: next.staffCap })} · ${t("new rooms: {list}", { list: next.rooms.filter((r) => !C.level[co.level].rooms.includes(r)).map((r) => t(C.room[r].name)).join(", ") })}`}
              line={<><span className="biz-ic" title={t("Room for {n} people", { n: next.staffCap })}><Icon name="staff" size={14} />+{next.staffCap - C.level[co.level].staffCap}</span><span className="biz-ic num" title={t("Rent")}><Icon name="coin" size={14} />{t("{pay}/wk", { pay: money(next.rent * idx) })}</span></>}
              verb={t("Move · {m}", { m: money(next.moveCost * idx) })}
              price={next.moveCost * idx}
              cash={co.cash}
              onBuy={() => run({ type: "moveStudio", level: next.id as "office" }, t("Welcome to your new {place}!", { place: t(next.name).toLowerCase() }))}
              buyId="move-studio"
            />
          )}
          {/* big bets once you're a studio: a shop in another town, or buying out a rival */}
          {(co.level === "studio" || co.level === "hq") && (() => {
            const year = Math.floor(game.week / 52) + 1965;
            const nextStore = stores.find((st) => !(co.stores ?? []).includes(st.id) && year >= st.year);
            const rivalsLeft = game.rivals.filter((r) => rivalActive(r, C.rival[r.id], game.week)).map((r) => ({ r, price: rivalPrice(game, r.id) })).sort((a, b) => a.price - b.price);
            const blocked = buyoutBlocked(game, co);
            const capped = !!blocked && blocked.startsWith("Regulators");
            return (
              <>
                {nextStore && (
                  <Bet
                    cardId="store-card"
                    name={t("Shop: {town}", { town: t(nextStore.name) })}
                    tip={`${t(nextStore.blurb)} · ${t("about {m}/wk now, more as fans grow", { m: money(storeWeekly(co, nextStore.id, game.week)) })} · ${t("your games reach {p}% further", { p: Math.round(nextStore.reach * 100) })}${(co.stores ?? []).length ? ` · ${tp((co.stores ?? []).length, "you have {n} shop", "you have {n} shops")}` : ""}`}
                    line={<><span className="biz-ic num"><Icon name="coin" size={14} />{t("≈{m}/wk", { m: money(storeWeekly(co, nextStore.id, game.week)) })}</span><span className="biz-ic num"><Icon name="fans" size={14} />+{nextStore.fans.toLocaleString("en-US")}</span><span className="biz-ic num"><Icon name="trend" size={14} />+{Math.round(nextStore.reach * 100)}%</span></>}
                    verb={t("Open · {m}", { m: money(nextStore.cost * idx) })}
                    price={nextStore.cost * idx}
                    cash={co.cash}
                    onBuy={() => run({ type: "openStore", store: nextStore.id }, t("Shop open: {town}", { town: t(nextStore.name) }))}
                    buyId="open-store"
                  />
                )}
                {capped && <span className="muted" style={{ fontSize: "0.85rem" }} data-testid="buyout-max">{blockedText(blocked)}</span>}
                {!capped && rivalsLeft.slice(0, 2).map(({ r, price }) => (
                  <Bet
                    key={r.id}
                    cardId="buyout-card"
                    name={C.rival[r.id].name}
                    tip={`${tp(r.released, "{n} game out", "{n} games out")} · ${t("they leave the market, their catalogue pays you weekly for six years, their fans join you")}`}
                    line={<><span className="biz-ic num"><Icon name="box" size={14} />{r.released}</span><span className="biz-ic num"><Icon name="calendar" size={14} />{t("{n} yr", { n: 6 })}</span><Icon name="fans" size={14} /></>}
                    verb={t("Buy · {m}", { m: money(price) })}
                    price={price}
                    cash={co.cash}
                    blocked={blocked ? blockedText(blocked) : undefined}
                    onBuy={() => run({ type: "acquireRival", rival: r.id }, t("{rival} is yours", { rival: C.rival[r.id].name }))}
                    buyId="buyout"
                  />
                ))}
              </>
            );
          })()}
          {/* buildings next door: from the studio on, the lot has room for more */}
          {annexes.map((a) => {
            const owned = (co.annexes ?? []).some((x) => x.kind === a.id);
            const order = ["garage", "office", "studio", "hq"];
            const ready = order.indexOf(co.level) >= order.indexOf(a.level);
            if (owned || !ready) return null;
            const cost = a.cost * idx;
            return (
              <Bet
                key={a.id}
                name={t(a.name)}
                tip={`${t(a.blurb)} · ${t("rooms: {list}", { list: a.rooms.map((r) => t(C.room[r].name)).join(", ") })}`}
                line={<><span className="biz-ic num"><Icon name="staff" size={14} />+{a.staffCap}</span><span className="biz-ic num" title={t("Rent")}><Icon name="coin" size={14} />{t("{pay}/wk", { pay: money(a.rent * idx) })}</span></>}
                verb={t("Buy · {m}", { m: money(cost) })}
                price={cost}
                cash={co.cash}
                onBuy={() => run({ type: "buyAnnex", annex: a.id }, t("{name} is yours", { name: t(a.name) }))}
                buyId={`buy-${a.id}`}
              />
            );
          })}
        </div>
      </div>
    </Sheet>
  );
}

/** A big purchase: its name, one line of icon facts, and the price on the button (the rest on hover). */
function Bet({ name, tip, line, verb, price, cash, onBuy, buyId, cardId, blocked }: { name: string; tip: string; line: ReactNode; verb: string; price: number; cash: number; onBuy: () => void; buyId: string; cardId?: string; blocked?: string }) {
  const short = cash < price;
  return (
    <div className="card stack biz-card" data-testid={cardId} title={tip}>
      <strong>{name}</strong>
      <div className="spread">
        <div className="biz-line">{line}</div>
        <button className="btn small primary" style={{ flex: "none" }} disabled={short || !!blocked} onClick={onBuy} data-testid={buyId} title={blocked ?? (short ? t("You need {m} more.", { m: money(price - cash) }) : undefined)}>{verb}</button>
      </div>
      {blocked && <span className="muted" style={{ fontSize: "0.82rem" }}>{blocked}</span>}
    </div>
  );
}

/** The R&D budget: a share of sales that turns into research points every week. */
function RdBudget() {
  const game = useGame((s) => s.game)!;
  const cmd = useGame((s) => s.cmd);
  const co = me(game)!;
  const share = co.rdShare ?? 0;
  const weeklySales = co.ledger.slice(-13).reduce((sum, w) => sum + (w.byCat.sales ?? 0), 0) / 13;
  const perPoint = BALANCE.rdPointCost * priceIndex(game.week) * eraCostMult(yearOf(game.week));
  const pointsYear = Math.round((weeklySales * share * 52) / perPoint);
  return (
    <div className="card stack biz-card" data-testid="rd-budget" title={t("Turns a share of sales into research points.")}>
      <span className="spread">
        <span className="biz-ic"><Icon name="research" size={16} color="var(--teal)" /><strong>{t("R&D budget")}</strong> <Why title={t("R&D budget")} lines={[{ label: t("Per week"), value: money(weeklySales * share) }, { label: t("Research points a year"), value: pointsYear }, { label: t("One point now"), value: money(perPoint) }]} note={t("Points buy perks, market studies and your house rules engine. They cost more each era; R&D comes off taxable profit.")} /></span>
        <span className="biz-line"><strong className="num" style={{ color: "var(--ink)" }}>{Math.round(share * 100)}%</strong>{share > 0 && <span className="biz-ic num" title={t("Research points a year")}><Icon name="research" size={13} />{t("≈{n}/yr", { n: pointsYear })}</span>}</span>
      </span>
      <input type="range" className="slider" min={0} max={BALANCE.rdMax} step={0.01} value={share} onChange={(e) => void cmd({ type: "setRd", share: Number(e.target.value) })} aria-label={t("R&D budget, share of sales")} />
    </div>
  );
}
